import { useState, useRef, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";

/**
 * 4-step wizard for importing a PGR PDF:
 *   1. Upload   — pick PDF, show size/preview, click "Analisar com IA"
 *   2. Extracting — loading state (Gemini call typically 20-60s)
 *   3. Review   — editable form pre-filled from extracted JSON
 *   4. Done     — summary with links to created entities
 */
export default function PgrImport() {
  const navigate = useNavigate();
  const { companyId } = useParams();          // when set, we COMPLEMENT instead of create
  const [step, setStep] = useState(1);
  const [file, setFile] = useState(null);
  const [extracted, setExtracted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [summary, setSummary] = useState(null);
  const [existingCompany, setExistingCompany] = useState(null);
  const fileRef = useRef();

  useEffect(() => {
    if (!companyId) return;
    api.get(`/companies/${companyId}`).then((r) => setExistingCompany(r.data)).catch(() => {});
  }, [companyId]);

  const onPick = (f) => {
    if (!f) return;
    if (f.type !== "application/pdf") {
      toast.error("Apenas arquivos PDF são aceitos.");
      return;
    }
    if (f.size > 50 * 1024 * 1024) {
      toast.error("Arquivo maior que 50MB.");
      return;
    }
    setFile(f);
    setError("");
  };

  const onExtract = async () => {
    if (!file) return;
    setStep(2);
    setBusy(true);
    setError("");
    try {
      const fd = new FormData();
      fd.append("file", file);
      // 1) Kick off async job (returns {job_id} in <5s — well under Cloudflare's 100s)
      const start = await api.post("/import/pgr/extract", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 60000,
      });
      const jobId = start.data.job_id;

      // 2) Poll every 4s for up to 5 min (PGR with 200 pages takes ~3 min)
      const MAX_TRIES = 75;
      const POLL_MS = 4000;
      let extractedData = null;
      for (let i = 0; i < MAX_TRIES; i++) {
        await new Promise((r) => setTimeout(r, POLL_MS));
        const s = await api.get(`/import/pgr/extract/status/${jobId}`);
        if (s.data.status === "done") {
          extractedData = s.data.data;
          break;
        }
        if (s.data.status === "error") {
          throw new Error(s.data.error || "Erro na análise do PGR.");
        }
      }
      if (!extractedData) {
        throw new Error("Análise demorou mais que o esperado. Tente novamente com um PDF menor ou contate o suporte.");
      }

      // 3) Merge company data when complementing
      if (existingCompany) {
        const fillIfEmpty = (k) => extractedData.empresa[k] || existingCompany[k] || "";
        Object.keys(existingCompany).forEach((k) => {
          if (k in extractedData.empresa && !extractedData.empresa[k]) {
            extractedData.empresa[k] = existingCompany[k];
          }
        });
        extractedData.empresa.razao_social = fillIfEmpty("razao_social");
        extractedData.empresa.cnpj = fillIfEmpty("cnpj");
      }
      setExtracted(extractedData);
      setStep(3);
    } catch (err) {
      setError(err.response?.data?.detail || err.message);
      setStep(1);
    } finally {
      setBusy(false);
    }
  };

  const onCommit = async (cfg) => {
    setBusy(true);
    try {
      const payload = {
        company_id: companyId || null,
        empresa: extracted.empresa,
        ghes: extracted.ghes,
        plano_acao: extracted.plano_acao,
        riscos_psicossociais: extracted.riscos_psicossociais,
        funcionarios: extracted.funcionarios,
        create_assessments: cfg.create_assessments,
        invite_employees: cfg.invite_employees,
      };
      const r = await api.post("/import/pgr/commit", payload);
      setSummary(r.data);
      setStep(4);
      toast.success("Importação concluída com sucesso!");
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setBusy(false);
    }
  };

  const isComplement = Boolean(companyId);

  return (
    <div className="space-y-6" data-testid="pgr-import-page">
      <div>
        <div className="overline">{isComplement ? "Complementar via PGR" : "Importar PGR"}</div>
        <h1 className="font-display text-3xl font-extrabold text-slate-900 mt-1">
          {isComplement && existingCompany
            ? `Importar PGR para ${existingCompany.razao_social}`
            : "Importação inteligente de PGR"}
        </h1>
        <p className="text-sm text-slate-600 mt-2 max-w-3xl">
          Envie o PDF do PGR e a IA Gemini vai extrair automaticamente os dados da
          empresa, GHEs/setores, riscos psicossociais identificados, plano de ação
          e lista de funcionários (se houver). Você revisa antes de confirmar.
        </p>
      </div>

      <Stepper step={step} />

      {step === 1 && (
        <StepUpload
          file={file}
          onPick={onPick}
          fileRef={fileRef}
          onExtract={onExtract}
          error={error}
          isComplement={isComplement}
        />
      )}

      {step === 2 && <StepExtracting />}

      {step === 3 && extracted && (
        <StepReview
          extracted={extracted}
          setExtracted={setExtracted}
          onCommit={onCommit}
          busy={busy}
          isComplement={isComplement}
        />
      )}

      {step === 4 && summary && (
        <StepDone summary={summary} navigate={navigate} isComplement={isComplement} />
      )}
    </div>
  );
}

function Stepper({ step }) {
  const labels = ["Upload", "Análise IA", "Revisão", "Confirmação"];
  return (
    <div className="flex items-center gap-2" data-testid="pgr-stepper">
      {labels.map((label, i) => {
        const n = i + 1;
        const active = step === n;
        const done = step > n;
        return (
          <div key={label} className="flex items-center gap-2 flex-1">
            <div className={`h-8 w-8 flex items-center justify-center text-xs font-bold border-2 transition-colors ${
              done ? "bg-emerald-900 text-white border-emerald-900"
                   : active ? "bg-white text-emerald-900 border-emerald-900"
                            : "bg-white text-slate-400 border-slate-300"}`}>
              {done ? <i className="fa-solid fa-check"></i> : n}
            </div>
            <div className={`text-xs uppercase tracking-wider font-bold flex-1 ${
              active ? "text-emerald-900" : done ? "text-slate-700" : "text-slate-400"}`}>
              {label}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function StepUpload({ file, onPick, fileRef, onExtract, error, isComplement }) {
  return (
    <div className="card space-y-4" data-testid="pgr-step-upload">
      <div
        onClick={() => fileRef.current?.click()}
        onDragOver={(e) => e.preventDefault()}
        onDrop={(e) => { e.preventDefault(); onPick(e.dataTransfer.files[0]); }}
        className="border-2 border-dashed border-slate-300 hover:border-emerald-900 hover:bg-emerald-50/50 cursor-pointer p-12 text-center transition-colors"
        data-testid="pgr-dropzone">
        <i className="fa-solid fa-cloud-arrow-up text-5xl text-slate-400 mb-3"></i>
        <div className="font-display text-lg font-bold text-slate-700">
          {file ? file.name : "Arraste o PDF do PGR ou clique para selecionar"}
        </div>
        <div className="text-xs text-slate-500 mt-2">
          Apenas PDF · até 50MB · ~30 a 200 páginas
        </div>
        <input
          ref={fileRef} type="file" accept="application/pdf" className="hidden"
          data-testid="pgr-file-input"
          onChange={(e) => onPick(e.target.files[0])}
        />
      </div>

      {file && (
        <div className="border border-emerald-200 bg-emerald-50/60 p-4 flex items-center justify-between">
          <div>
            <div className="font-bold text-emerald-900" data-testid="pgr-file-name">{file.name}</div>
            <div className="text-xs text-slate-600 mt-1">
              {(file.size / 1024 / 1024).toFixed(2)} MB · {file.type}
            </div>
          </div>
          <button
            onClick={onExtract}
            data-testid="pgr-extract-btn"
            className="bg-emerald-900 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-emerald-800 transition-colors">
            <i className="fa-solid fa-wand-magic-sparkles mr-2"></i>Analisar com IA
          </button>
        </div>
      )}

      {error && (
        <div className="border border-rose-300 bg-rose-50 text-rose-900 p-4 text-sm" data-testid="pgr-error">
          <i className="fa-solid fa-circle-exclamation mr-2"></i>{error}
        </div>
      )}

      <div className="border border-slate-200 bg-slate-50 p-4 text-xs text-slate-600 leading-relaxed">
        <div className="font-bold uppercase tracking-wider text-slate-700 mb-2">O que será extraído</div>
        <ul className="list-disc pl-5 space-y-1">
          <li>{isComplement ? "Dados complementares da empresa (campos vazios são preenchidos)" : "Dados completos da empresa (razão social, CNPJ, endereço, CNAE, grau de risco, responsável técnico)"}</li>
          <li>GHEs / Setores com função e número de trabalhadores</li>
          <li>Riscos psicossociais classificados nas 9 dimensões da NR-01</li>
          <li>Plano de ação (medidas, responsáveis, prazos, prioridades)</li>
          <li>Lista nominal de funcionários (quando presente no PGR)</li>
        </ul>
      </div>
    </div>
  );
}

function StepExtracting() {
  return (
    <div className="card text-center py-16" data-testid="pgr-step-extracting">
      <div className="inline-block animate-spin h-16 w-16 border-4 border-emerald-900 border-t-transparent mb-6"></div>
      <h2 className="font-display text-2xl font-extrabold text-slate-900">Analisando seu PGR…</h2>
      <p className="text-sm text-slate-600 mt-3 max-w-md mx-auto">
        A IA está lendo página por página, identificando empresa, GHEs, riscos
        psicossociais e plano de ação. Isso pode levar de 20 a 90 segundos
        dependendo do tamanho do documento.
      </p>
    </div>
  );
}

function StepReview({ extracted, setExtracted, onCommit, busy, isComplement }) {
  const [createAssessments, setCreateAssessments] = useState(true);
  const [inviteEmployees, setInviteEmployees] = useState(false);

  const update = (path, value) => {
    const next = JSON.parse(JSON.stringify(extracted));
    const segs = path.split(".");
    let cur = next;
    for (let i = 0; i < segs.length - 1; i++) cur = cur[segs[i]];
    cur[segs[segs.length - 1]] = value;
    setExtracted(next);
  };

  const updateArray = (key, index, field, value) => {
    const next = JSON.parse(JSON.stringify(extracted));
    next[key][index][field] = value;
    setExtracted(next);
  };

  const removeArrayItem = (key, index) => {
    const next = JSON.parse(JSON.stringify(extracted));
    next[key].splice(index, 1);
    setExtracted(next);
  };

  const e = extracted.empresa;
  const employeesWithEmail = extracted.funcionarios.filter((f) => f.email).length;

  return (
    <div className="space-y-6" data-testid="pgr-step-review">
      {/* Empresa */}
      <div className="card">
        <div className="flex items-baseline justify-between mb-4">
          <h2 className="font-display text-xl font-extrabold text-slate-900">
            {isComplement ? "Dados a complementar da empresa" : "Dados da empresa"}
          </h2>
          <span className="text-[10px] uppercase tracking-wider bg-emerald-100 text-emerald-900 border border-emerald-300 px-2 py-1">
            {isComplement ? "Patch · só campos vazios" : "Auto-criar"}
          </span>
        </div>
        <div className="grid md:grid-cols-2 gap-3 text-sm">
          {[
            ["razao_social", "Razão social"], ["nome_fantasia", "Nome fantasia"],
            ["cnpj", "CNPJ"], ["cnae", "CNAE"], ["grau_risco", "Grau de risco"],
            ["cep", "CEP"], ["endereco", "Endereço"], ["numero", "Número"],
            ["complemento", "Complemento"], ["bairro", "Bairro"],
            ["cidade", "Cidade"], ["uf", "UF"],
            ["responsavel_tecnico", "Responsável técnico"],
            ["responsavel_tecnico_registro", "Registro (CREA/etc)"],
          ].map(([key, label]) => (
            <label key={key} className="block">
              <div className="overline mb-1">{label}</div>
              <input
                value={e[key] || ""}
                onChange={(ev) => update(`empresa.${key}`, ev.target.value)}
                data-testid={`pgr-empresa-${key}`}
                className="w-full border border-slate-300 px-3 py-2 focus:border-emerald-700 focus:outline-none"
              />
            </label>
          ))}
        </div>
      </div>

      {/* GHEs */}
      <EditableSection
        title="GHEs / Setores"
        badge={`${extracted.ghes.length} encontrados`}
        items={extracted.ghes}
        emptyHint="Nenhum GHE identificado. Adicione manualmente se necessário."
        fields={[
          ["ghe_setor", "GHE / Setor", "text"],
          ["funcao", "Função", "text"],
          ["n_trabalhadores", "Nº trabalhadores", "number"],
          ["responsavel", "Responsável", "text"],
        ]}
        onUpdate={(i, f, v) => updateArray("ghes", i, f, v)}
        onRemove={(i) => removeArrayItem("ghes", i)}
        testidPrefix="pgr-ghe"
      />

      {/* Riscos psicossociais */}
      <EditableSection
        title="Riscos psicossociais identificados"
        badge={`${extracted.riscos_psicossociais.length} encontrados`}
        items={extracted.riscos_psicossociais}
        emptyHint="Nenhum risco psicossocial mapeado no PGR."
        fields={[
          ["dimensao", "Dimensão NR-01", "text"],
          ["descricao", "Descrição", "text"],
          ["ghe_relacionado", "GHE relacionado", "text"],
          ["nivel", "Nível", "text"],
        ]}
        onUpdate={(i, f, v) => updateArray("riscos_psicossociais", i, f, v)}
        onRemove={(i) => removeArrayItem("riscos_psicossociais", i)}
        testidPrefix="pgr-risco"
      />

      {/* Plano de ação */}
      <EditableSection
        title="Plano de ação"
        badge={`${extracted.plano_acao.length} ações · Auto-importar`}
        badgeTone="emerald"
        items={extracted.plano_acao}
        emptyHint="Nenhuma ação encontrada."
        fields={[
          ["acao", "Ação", "text"],
          ["responsavel", "Responsável", "text"],
          ["prazo", "Prazo", "text"],
          ["prioridade", "Prioridade", "select", ["Crítica", "Alta", "Média", "Baixa"]],
        ]}
        onUpdate={(i, f, v) => updateArray("plano_acao", i, f, v)}
        onRemove={(i) => removeArrayItem("plano_acao", i)}
        testidPrefix="pgr-acao"
      />

      {/* Funcionários */}
      <EditableSection
        title="Lista de funcionários"
        badge={`${extracted.funcionarios.length} encontrados · ${employeesWithEmail} com e-mail`}
        items={extracted.funcionarios}
        emptyHint="Nenhuma lista nominal encontrada — você poderá adicionar e-mails manualmente depois."
        fields={[
          ["nome", "Nome", "text"],
          ["matricula", "Matrícula", "text"],
          ["funcao", "Função", "text"],
          ["email", "E-mail", "email"],
        ]}
        onUpdate={(i, f, v) => updateArray("funcionarios", i, f, v)}
        onRemove={(i) => removeArrayItem("funcionarios", i)}
        testidPrefix="pgr-func"
      />

      {/* Confirmation toggles */}
      <div className="card border-2 border-emerald-300 bg-emerald-50/40">
        <h2 className="font-display text-lg font-extrabold text-slate-900 mb-3">Opções de importação</h2>
        <div className="space-y-3">
          <Toggle
            checked={createAssessments}
            onChange={setCreateAssessments}
            label="Criar uma avaliação NR-01 para cada GHE"
            sub="Cada GHE vira uma avaliação independente, pronta para receber respostas."
            testid="pgr-opt-assessments"
          />
          <Toggle
            checked={inviteEmployees}
            onChange={setInviteEmployees}
            label="Enviar convites por e-mail aos funcionários encontrados"
            sub={employeesWithEmail > 0
              ? `${employeesWithEmail} convite(s) serão disparados imediatamente.`
              : "Nenhum funcionário com e-mail — opção sem efeito."}
            disabled={employeesWithEmail === 0}
            testid="pgr-opt-invite"
          />
        </div>
        <div className="mt-5 pt-5 border-t border-slate-200 flex flex-wrap gap-3">
          <button
            disabled={busy}
            onClick={() => onCommit({ create_assessments: createAssessments, invite_employees: inviteEmployees })}
            data-testid="pgr-commit-btn"
            className="bg-emerald-900 text-white px-6 py-3 text-sm font-bold uppercase tracking-wider hover:bg-emerald-800 transition-colors disabled:opacity-50">
            <i className="fa-solid fa-check mr-2"></i>
            {busy ? "Importando…" : isComplement ? "Complementar empresa" : "Confirmar importação"}
          </button>
        </div>
      </div>
    </div>
  );
}

function EditableSection({ title, badge, badgeTone = "slate", items, emptyHint, fields, onUpdate, onRemove, testidPrefix }) {
  const tone = badgeTone === "emerald"
    ? "bg-emerald-100 text-emerald-900 border-emerald-300"
    : "bg-slate-100 text-slate-700 border-slate-300";
  return (
    <div className="card" data-testid={`${testidPrefix}-section`}>
      <div className="flex items-baseline justify-between mb-4">
        <h2 className="font-display text-xl font-extrabold text-slate-900">{title}</h2>
        <span className={`text-[10px] uppercase tracking-wider border px-2 py-1 ${tone}`}>{badge}</span>
      </div>
      {items.length === 0 ? (
        <div className="text-sm text-slate-500 italic">{emptyHint}</div>
      ) : (
        <div className="space-y-3">
          {items.map((item, i) => (
            <div key={i} className="border border-slate-200 p-3 grid md:grid-cols-4 gap-2"
                 data-testid={`${testidPrefix}-row-${i}`}>
              {fields.map(([k, label, type, options]) => (
                <label key={k} className="block">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">{label}</div>
                  {type === "select" ? (
                    <select value={item[k] || ""}
                            onChange={(e) => onUpdate(i, k, e.target.value)}
                            data-testid={`${testidPrefix}-${i}-${k}`}
                            className="w-full border border-slate-300 px-2 py-1.5 text-sm">
                      {options.map((o) => <option key={o} value={o}>{o}</option>)}
                    </select>
                  ) : (
                    <input
                      type={type}
                      value={item[k] || ""}
                      onChange={(e) => onUpdate(i, k, type === "number" ? parseInt(e.target.value || "0", 10) : e.target.value)}
                      data-testid={`${testidPrefix}-${i}-${k}`}
                      className="w-full border border-slate-300 px-2 py-1.5 text-sm"
                    />
                  )}
                </label>
              ))}
              <div className="md:col-span-4 flex justify-end">
                <button onClick={() => onRemove(i)}
                        data-testid={`${testidPrefix}-${i}-remove`}
                        className="text-xs text-rose-700 hover:text-rose-900">
                  <i className="fa-solid fa-trash mr-1"></i>Remover
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Toggle({ checked, onChange, label, sub, disabled, testid }) {
  return (
    <label className={`flex items-start gap-3 ${disabled ? "opacity-50" : "cursor-pointer"}`}>
      <button
        type="button" role="switch" aria-checked={checked} disabled={disabled}
        onClick={() => !disabled && onChange(!checked)}
        data-testid={testid}
        className={`relative inline-flex h-6 w-11 items-center transition-colors flex-shrink-0 mt-0.5 ${
          checked ? "bg-emerald-900" : "bg-slate-300"}`}>
        <span className={`inline-block h-5 w-5 bg-white transform transition-transform ${checked ? "translate-x-5" : "translate-x-0.5"}`}></span>
      </button>
      <div>
        <div className="text-sm font-bold text-slate-900">{label}</div>
        {sub && <div className="text-xs text-slate-600 mt-0.5">{sub}</div>}
      </div>
    </label>
  );
}

function StepDone({ summary, navigate, isComplement }) {
  return (
    <div className="card text-center py-12 space-y-4" data-testid="pgr-step-done">
      <i className="fa-solid fa-circle-check text-emerald-600 text-6xl"></i>
      <h2 className="font-display text-3xl font-extrabold text-slate-900">
        Importação concluída
      </h2>
      <div className="grid md:grid-cols-3 gap-4 max-w-2xl mx-auto pt-4">
        <div className="border border-slate-200 p-4">
          <div className="overline mb-1">Empresa</div>
          <div className="font-display text-2xl font-extrabold text-emerald-900">
            {summary.company_action === "created" ? "Criada" : "Atualizada"}
          </div>
        </div>
        <div className="border border-slate-200 p-4">
          <div className="overline mb-1">Avaliações criadas</div>
          <div className="font-display text-2xl font-extrabold text-emerald-900" data-testid="pgr-summary-assessments">
            {summary.assessment_ids.length}
          </div>
        </div>
        <div className="border border-slate-200 p-4">
          <div className="overline mb-1">Ações de plano</div>
          <div className="font-display text-2xl font-extrabold text-emerald-900" data-testid="pgr-summary-actions">
            {summary.action_plan_ids.length}
          </div>
        </div>
      </div>
      {summary.invites_dispatched > 0 && (
        <div className="text-sm text-slate-600">
          <i className="fa-solid fa-envelope mr-2"></i>
          {summary.invites_dispatched} convite(s) enviados aos funcionários.
        </div>
      )}
      <div className="pt-4 flex flex-wrap justify-center gap-3">
        <button
          onClick={() => navigate(`/app/empresas/${summary.company_id}/mapa`)}
          data-testid="pgr-done-goto-company"
          className="bg-emerald-900 text-white px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-emerald-800">
          <i className="fa-solid fa-building mr-2"></i>Abrir empresa
        </button>
        {summary.assessment_ids.length > 0 && (
          <button
            onClick={() => navigate(`/app/avaliacoes/${summary.assessment_ids[0]}`)}
            data-testid="pgr-done-goto-assessment"
            className="border border-emerald-900 text-emerald-900 px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-emerald-50">
            <i className="fa-solid fa-clipboard-list mr-2"></i>Ver primeira avaliação
          </button>
        )}
        <button
          onClick={() => navigate("/app/empresas")}
          className="border border-slate-300 text-slate-700 px-5 py-2.5 text-sm font-bold uppercase tracking-wider hover:bg-slate-50">
          Voltar para empresas
        </button>
      </div>
    </div>
  );
}
